// import { useEffect } from "react";

// import { getCurrentUser, login as loginApi, logout as logoutApi, refreshAccessToken } from "../api/auth";
// import { useAuthStore } from "../stores/authStore";
// import type { LoginRequest } from "../types/auth";

// function decodeTokenExpiry(token: string | null): number | null {
//   if (!token) {
//     return null;
//   }
//   try {
//     const [, payload] = token.split(".");
//     if (!payload) {
//       return null;
//     }
//     const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
//     const decoded = JSON.parse(window.atob(normalizedPayload));
//     return typeof decoded.exp === "number" ? decoded.exp : null;
//   } catch {
//     return null;
//   }
// }

// export function useAuth() {
//   const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
//   const isLoading = useAuthStore((state) => state.isLoading);
//   const isInitialized = useAuthStore((state) => state.isInitialized);
//   const user = useAuthStore((state) => state.user);
//   const accessToken = useAuthStore((state) => state.accessToken);
//   const refreshToken = useAuthStore((state) => state.refreshToken);
//   const setSession = useAuthStore((state) => state.setSession);
//   const setUser = useAuthStore((state) => state.setUser);
//   const updateTokens = useAuthStore((state) => state.updateTokens);
//   const setLoading = useAuthStore((state) => state.setLoading);
//   const setInitialized = useAuthStore((state) => state.setInitialized);
//   const clearSession = useAuthStore((state) => state.clearSession);

//   const login = async (payload: LoginRequest) => {
//     setLoading(true);
//     try {
//       const session = await loginApi(payload);
//       setSession(session);
//       return session;
//     } catch (error) {
//       setLoading(false);
//       throw error;
//     }
//   };

//   const logout = async () => {
//     try {
//       if (refreshToken) {
//         await logoutApi(refreshToken);
//       }
//     } finally {
//       clearSession();
//       useAuthStore.persist.clearStorage();
//       window.localStorage.clear();
//       window.sessionStorage.clear();
//     }
//   };

//   const initialize = async () => {
//     if (isLoading || isInitialized) {
//       return;
//     }
//     if (!refreshToken) {
//       setInitialized(true);
//       return;
//     }

//     setLoading(true);
//     try {
//       let nextAccessToken = accessToken;
//       const exp = decodeTokenExpiry(accessToken);
//       const now = Math.floor(Date.now() / 1000);
//       if (!exp || exp - now <= 120) {
//         const tokens = await refreshAccessToken(refreshToken);
//         nextAccessToken = tokens.access;
//         updateTokens(tokens);
//       }

//       if (!user || !nextAccessToken) {
//         const currentUser = await getCurrentUser();
//         setUser(currentUser);
//       }
//       setInitialized(true);
//     } catch {
//       clearSession();
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     if (!refreshToken || !isInitialized) {
//       return;
//     }
//     const interval = window.setInterval(async () => {
//       const exp = decodeTokenExpiry(useAuthStore.getState().accessToken);
//       const currentRefreshToken = useAuthStore.getState().refreshToken;
//       const now = Math.floor(Date.now() / 1000);
//       if (!exp || !currentRefreshToken || exp - now > 120) {
//         return;
//       }
//       try {
//         const tokens = await refreshAccessToken(currentRefreshToken);
//         useAuthStore.getState().updateTokens(tokens);
//       } catch {
//         useAuthStore.getState().clearSession();
//       }
//     }, 30000);

//     return () => window.clearInterval(interval);
//   }, [isInitialized, refreshToken]);

//   return {
//     isAuthenticated,
//     isLoading,
//     isInitialized,
//     user,
//     login,
//     logout,
//     initialize,
//     isVessel: user?.type === "crew",
//     isOffice: user?.type === "office",
//     isMaster: (user?.rank ?? "").toUpperCase() === "MASTER" || (user?.rank ?? "").toUpperCase() === "CAPTAIN",
//     isCrew: user?.type === "crew",
//     formIds: user?.form_ids ?? [],
//     processIds: user?.process_ids ?? [],
//     hasForm: (formId: string) => (user?.form_ids ?? []).includes(formId),
//     hasProcess: (processId: string) => (user?.process_ids ?? []).includes(processId),
//   };
// }


//-------------------------------------- version 2.0.0 --------------------------------------

import { useEffect } from "react";

import { getCurrentUser, login as loginApi, logout as logoutApi, refreshAccessToken } from "../api/auth";
import { hasPermission } from "../lib/permissions";
import { useAuthStore } from "../stores/authStore";
import type { LoginRequest } from "../types/auth";

function decodeTokenExpiry(token: string | null): number | null {
  if (!token) {
    return null;
  }
  try {
    const [, payload] = token.split(".");
    if (!payload) {
      return null;
    }
    const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = JSON.parse(window.atob(normalizedPayload));
    return typeof decoded.exp === "number" ? decoded.exp : null;
  } catch {
    return null;
  }
}

export function useAuth() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);
  const isInitialized = useAuthStore((state) => state.isInitialized);
  const user = useAuthStore((state) => state.user);
  const accessToken = useAuthStore((state) => state.accessToken);
  const refreshToken = useAuthStore((state) => state.refreshToken);
  const setSession = useAuthStore((state) => state.setSession);
  const setUser = useAuthStore((state) => state.setUser);
  const updateTokens = useAuthStore((state) => state.updateTokens);
  const setLoading = useAuthStore((state) => state.setLoading);
  const setInitialized = useAuthStore((state) => state.setInitialized);
  const clearSession = useAuthStore((state) => state.clearSession);

  const login = async (payload: LoginRequest) => {
    setLoading(true);
    try {
      const session = await loginApi(payload);
      setSession(session);
      return session;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const logout = async () => {
    const currentRefreshToken = refreshToken;
    const currentAccessToken = accessToken;

    clearSession();
    useAuthStore.persist.clearStorage();
    window.localStorage.clear();
    window.sessionStorage.clear();

    if (currentRefreshToken) {
      void Promise.resolve(logoutApi(currentRefreshToken, currentAccessToken)).catch(() => {
        // Logout must stay responsive even if the server is slow or unreachable.
      });
    }
  };

  const initialize = async () => {
    if (isLoading || isInitialized) {
      return;
    }
    if (!refreshToken) {
      setInitialized(true);
      return;
    }

    setLoading(true);
    try {
      let nextAccessToken = accessToken;
      const exp = decodeTokenExpiry(accessToken);
      const now = Math.floor(Date.now() / 1000);
      if (!exp || exp - now <= 120) {
        const tokens = await refreshAccessToken(refreshToken);
        nextAccessToken = tokens.access;
        updateTokens(tokens);
      }

      if (!user || !nextAccessToken) {
        const currentUser = await getCurrentUser();
        setUser(currentUser);
      } else if (!Array.isArray(user.vessels)) {
        void getCurrentUser()
          .then((currentUser) => {
            useAuthStore.getState().setUser(currentUser);
          })
          .catch(() => {
            // Keep the persisted user when the background profile sync fails.
          });
      }
      setInitialized(true);
    } catch {
      clearSession();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!refreshToken || !isInitialized) {
      return;
    }
    const interval = window.setInterval(async () => {
      const exp = decodeTokenExpiry(useAuthStore.getState().accessToken);
      const currentRefreshToken = useAuthStore.getState().refreshToken;
      const now = Math.floor(Date.now() / 1000);
      if (!exp || !currentRefreshToken || exp - now > 120) {
        return;
      }
      try {
        const tokens = await refreshAccessToken(currentRefreshToken);
        useAuthStore.getState().updateTokens(tokens);
      } catch {
        useAuthStore.getState().clearSession();
      }
    }, 30000);

    return () => window.clearInterval(interval);
  }, [isInitialized, refreshToken]);

  return {
    isAuthenticated,
    isLoading,
    isInitialized,
    user,
    login,
    logout,
    initialize,
    isVessel: user?.type === "crew",
    isOffice: user?.type === "office",
    isMaster: (user?.rank ?? "").toUpperCase() === "MASTER" || (user?.rank ?? "").toUpperCase() === "CAPTAIN",
    isCrew: user?.type === "crew",
    formIds: user?.form_ids ?? [],
    processIds: user?.process_ids ?? [],
    hasForm: (formId: string) => hasPermission(user, { forms: [formId] }),
    hasProcess: (processId: string) => hasPermission(user, { processes: [processId] }),
  };
}

